// src/pages/AllBookingsPage.jsx

import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { isFutureBookingInOrgTimezone, formatInOrgTimezone } from "../utils/timezoneUtils";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSnackbar } from "notistack";
import {
  getAllBookingsAdmin,
  cancelAnyBookingAdmin,
} from "../services/bookingService";
import { useTimezone } from "../contexts/TimezoneContext";

export default function AllBookingsPage() {
  // short English comments only
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const { orgTimeZone } = useTimezone();

  // filters
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState("all");

  // dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  // tab state: "future" or "past"
  const [tabValue, setTabValue] = useState("future");

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchBookingsAdmin();
  }, []);

  async function fetchBookingsAdmin() {
    try {
      setError("");
      const data = await getAllBookingsAdmin();
      setBookings(data);
    } catch (err) {
      setError("Failed to load all bookings");
      enqueueSnackbar("Failed to load all bookings", { variant: "error" });
    }
  }

  function handleOpenDialog(bookingId) {
    setBookingToCancel(bookingId);
    setDialogOpen(true);
  }

  function handleCloseDialog() {
    setBookingToCancel(null);
    setDialogOpen(false);
  }

  async function handleConfirmCancel() {
    if (!bookingToCancel) return;
    try {
      await cancelAnyBookingAdmin(bookingToCancel);
      setBookings((prev) => prev.filter((b) => b._id !== bookingToCancel));
      enqueueSnackbar("Booking canceled successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to cancel booking", { variant: "error" });
    } finally {
      setDialogOpen(false);
      setBookingToCancel(null);
    }
  }

  // separate future vs past (using organization timezone)
  function isFutureBooking(b) {
    return isFutureBookingInOrgTimezone(b.endTime, orgTimeZone);
  }

  // filter + tab
  function filterBookings(list) {
    // list already includes all bookings
    let result = [...list];

    // tab-based filter
    if (tabValue === "future") {
      result = result.filter(isFutureBooking);
    } else if (tabValue === "past") {
      result = result.filter((b) => !isFutureBooking(b));
    }

    // room filter
    if (selectedRoom !== "all") {
      result = result.filter((b) => b.room && b.room._id === selectedRoom);
    }
    // date filter (using organization timezone)
    if (selectedDate) {
      const daySelected = moment.tz(selectedDate.toDate(), orgTimeZone).startOf("day");
      result = result.filter((b) => {
        const start = moment.tz(b.startTime, orgTimeZone).startOf("day");
        return start.isSame(daySelected, "day");
      });
    }
    // filter by user
    if (selectedUser !== "all") {
      result = result.filter((b) => b.user && b.user._id === selectedUser);
    }

    return result;
  }

  // gather unique rooms
  const uniqueRooms = [];
  // gather unique users
  const uniqueUsers = [];

  bookings.forEach((b) => {
    if (b.room && !uniqueRooms.find((r) => r._id === b.room._id)) {
      uniqueRooms.push(b.room);
    }
    if (b.user && !uniqueUsers.find((u) => u._id === b.user._id)) {
      uniqueUsers.push(b.user);
    }
  });

  // final list to display
  const filtered = filterBookings(bookings);

  function handleTabChange(event, newValue) {
    setTabValue(newValue);
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h4">
          All Bookings (Admin)
        </Typography>
        <Chip 
          label={`Times shown in ${orgTimeZone}`} 
          size="small" 
          variant="outlined" 
          color="info"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tab for future vs past */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Future Bookings" value="future" />
          <Tab label="Past Bookings" value="past" />
        </Tabs>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="filter-room-label">Filter by Room</InputLabel>
            <Select
              labelId="filter-room-label"
              label="Filter by Room"
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {uniqueRooms.map((room) => (
                <MenuItem key={room._id} value={room._id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Filter by User */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="filter-user-label">Filter by User</InputLabel>
            <Select
              labelId="filter-user-label"
              label="Filter by User"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {uniqueUsers.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Filter by Date"
              value={selectedDate}
              onChange={(newVal) => setSelectedDate(newVal)}
              slots={{
                textField: TextField
              }}
              slotProps={{
                textField: {
                  sx: { minWidth: 220 }
                }
              }}
            />
          </LocalizationProvider>

          <Button
            variant="outlined"
            onClick={() => {
              setSelectedRoom("all");
              setSelectedDate(null);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {filtered.length === 0 ? (
        <Typography>No bookings found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((b) => {
            const isFuture = isFutureBooking(b);
            return (
              <Grid item xs={12} sm={6} md={4} key={b._id}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6">
                      {b.room?.name || "Unknown Room"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Start: {formatInOrgTimezone(b.startTime, orgTimeZone)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      End: {formatInOrgTimezone(b.endTime, orgTimeZone)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Booked by: {b.user?.username || "[Unknown]"}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    {isFuture ? (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleOpenDialog(b._id)}
                      >
                        Cancel
                      </Button>
                    ) : (
                      <Typography color="text.secondary" sx={{ ml: 2 }}>
                        Ended
                      </Typography>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* confirmation dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this booking?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            No
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
